import { useEffect, useState } from 'react';
import type { ApercuSuppression } from '@mlc/shared';
import { apercuSuppression, deleteRecipe, recipePdfUrl } from './api';
import { unpinRecipe } from './activeRecipes';
import { navigate, paths } from './router';
import { Banner, Button, LinkButton, cardClasses } from './ui';

/**
 * La suppression définitive d'une recette (US-83) — **le seul endroit de
 * l'application où une donnée se perd**.
 *
 * ### Où elle vit, et pourquoi nulle part ailleurs
 *
 * Sur la fiche d'une recette **déjà archivée**, et seulement là (D17). La règle
 * fondatrice — « on archive, on ne supprime jamais » — n'est pas retirée, elle
 * est mise en amont : *archiver reste le geste, supprimer est ce qu'on fait à
 * ce qui est déjà rangé*. Deux gestes séparés dans le temps protègent mieux
 * qu'une saisie du titre à recopier, pénible dès la troisième fois et
 * mécanique dès la deuxième.
 *
 * Et **rien n'est grisé** : un membre ordinaire ne voit pas ce panneau, il ne
 * le voit pas désactivé. Même famille que « 404, jamais 403 » — une commande
 * grisée dit qu'elle existe pour quelqu'un d'autre.
 *
 * ### L'ordre à l'écran est une décision
 *
 * L'export vient **avant** le bouton qui rend l'export impossible. Le PDF
 * d'une recette seule existe depuis US-12 : la proposer ici ne coûte qu'un
 * lien, et c'est ce qui rend l'effacement immédiat tenable sans corbeille.
 *
 * ### Ce que le panneau dit, et qu'il est seul à savoir
 *
 * Le décompte est **calculé, pas estimé** (D16) : ce qui va disparaître, et ce
 * qui appartient à d'autres. « 3 réalisations de Claire » est ce qu'un
 * « Êtes-vous sûr ? » cache. Et la limite du mot « définitif » est écrite en
 * une ligne — les sauvegardes hebdomadaires gardent encore la recette
 * quelque temps. C'est la même famille que la phrase d'honnêteté d'US-49 :
 * *la seule promesse que le système ne peut pas tenir, il ne la fait pas*.
 */

export interface SuppressionDefinitiveProps {
  recipeId: string;
  title: string;
}

/**
 * « de Arthur » n'existe pas en français. L'élision se fait ici plutôt que dans
 * `shared/src/formatting.ts` : elle ne sert qu'à ce panneau, et l'application
 * n'écrit nulle part ailleurs « de » suivi d'un prénom libre — « d'après le
 * pot-au-feu d'Arthur » est composée côté fiche, sur un autre patron.
 */
function elider(texte: string): string {
  return texte.replace(/^de ([aeiouyàâäéèêëîïôöùûüh])/i, (_, premiere: string) => `d’${premiere}`);
}

/** Une ligne du décompte, écrite au singulier ou au pluriel selon ce qu'elle compte. */
function ligne(n: number, singulier: string, pluriel: string): string | null {
  if (n === 0) return null;
  return `${String(n)} ${n > 1 ? pluriel : singulier}`;
}

/**
 * Ce qui disparaît, en toutes lettres. Les réalisations sont **nommées** :
 * c'est le cœur de D16, et une somme anonyme (« 5 réalisations ») dirait
 * strictement moins que « 3 de Claire, 2 de vous ».
 */
function inventaire(apercu: ApercuSuppression): string[] {
  const parts: (string | null)[] = [
    ...apercu.realisations.map((part) => {
      const quoi = part.count > 1 ? 'réalisations' : 'réalisation';
      // Un auteur inconnu — une réalisation d'avant les comptes individuels —
      // se dit comme tel plutôt que d'être attribué à quelqu'un au hasard.
      const qui = part.display_name === null ? 'sans auteur connu' : `de ${part.display_name}`;
      return `${String(part.count)} ${quoi} ${elider(qui)}`;
    }),
    ligne(apercu.commentaires, 'commentaire', 'commentaires'),
    ligne(apercu.photos, 'photo', 'photos'),
    ligne(apercu.liens_de_partage_actifs, 'lien de partage public', 'liens de partage publics'),
    ligne(apercu.entrees_de_plan, 'entrée du plan de la semaine', 'entrées du plan de la semaine'),
  ];
  return parts.filter((part): part is string => part !== null);
}

export function SuppressionDefinitive({ recipeId, title }: SuppressionDefinitiveProps): React.JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [apercu, setApercu] = useState<ApercuSuppression | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  /**
   * Le décompte se demande à l'ouverture du panneau, pas au montage de la
   * fiche : il n'a de sens qu'au moment où l'on hésite, et le demander pour
   * chaque recette archivée qu'on ouvre serait une requête pour rien.
   */
  useEffect(() => {
    if (!ouvert) return;
    const controller = new AbortController();
    apercuSuppression(recipeId, controller.signal)
      .then(setApercu)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setErreur(e.message);
      });
    return () => { controller.abort(); };
  }, [ouvert, recipeId]);

  function supprimer(): void {
    setEnCours(true);
    setErreur(null);
    deleteRecipe(recipeId)
      .then(() => {
        // La recette peut être ouverte en mode cuisine sur un autre appareil ;
        // sur celui-ci, au moins, la barre « En cuisson » cesse d'y renvoyer.
        unpinRecipe(recipeId);
        navigate(paths.archives, { replace: true });
      })
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
        setEnCours(false);
      });
  }

  return (
    <section
      id="suppression"
      aria-labelledby={`suppression-${recipeId}`}
      className={cardClasses({ className: 'flex scroll-mt-8 flex-col gap-3' })}
    >
      <h2 id={`suppression-${recipeId}`} className="text-heading-3">Supprimer définitivement</h2>

      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      {!ouvert ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Ne redit pas « Cette recette est archivée » : le bandeau en tête
              de fiche le dit déjà, et deux fois la même phrase sur un même
              écran, c'est une phrase qu'on cesse de lire. Celle-ci part de
              l'archivage pour dire ce qui l'en distingue. */}
          <p className="max-w-reading text-sm text-foreground-secondary">
            L’archivage se défait d’un clic. La suppression, non : la recette,
            son journal et ses photos quittent l’application.
          </p>
          <Button type="button" variant="quiet" size="compact" onClick={() => { setOuvert(true); }}>
            Supprimer définitivement…
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Banner tone="warning">
            {apercu === null
              ? 'Décompte de ce qui va disparaître…'
              : `Supprimer « ${title} » effacera la recette, ses ingrédients, ses étapes et son journal.`}
          </Banner>

          {apercu !== null && (
            <>
              {inventaire(apercu).length > 0 && (
                <div className="flex flex-col gap-1">
                  <p className="text-label">Disparaîtront avec elle</p>
                  <ul className="list-disc pl-5 text-sm text-foreground-secondary">
                    {inventaire(apercu).map((part) => <li key={part}>{part}</li>)}
                  </ul>
                </div>
              )}

              {/* Une copie ne disparaît pas : elle perd son origine (D15). La
                  formuler comme une perte serait faux, la taire serait
                  incomplet — c'est une conséquence visible ailleurs dans la
                  bibliothèque, et elle se dit à part. */}
              {apercu.copies > 0 && (
                <p className="max-w-reading text-sm text-foreground-secondary">
                  {apercu.copies > 1
                    ? `${String(apercu.copies)} copies de cette recette resteront, entières, sans la mention « d’après ».`
                    : 'Une copie de cette recette restera, entière, sans la mention « d’après ».'}
                </p>
              )}

              {/* La limite du mot « définitif » (§6 du plan). Elle se dit ici
                  et pas ailleurs : c'est le seul moment où elle change quelque
                  chose pour celui qui lit. */}
              <p className="max-w-reading text-sm text-foreground-muted">
                Les sauvegardes hebdomadaires garderont encore cette recette quelque temps :
                restaurer une archive ancienne sur une base vide la ramènerait.
              </p>
            </>
          )}

          {/* **L'ordre compte** : on ne place pas un export après le bouton qui
              rend l'export impossible. */}
          <div className="flex flex-wrap items-center gap-2">
            <LinkButton href={recipePdfUrl(recipeId)} download variant="secondary" size="compact" className="w-fit">
              Exporter cette recette
            </LinkButton>
            <span className="flex-1" />
            <Button type="button" variant="ghost" size="compact" onClick={() => { setOuvert(false); }}>
              Annuler
            </Button>
            <Button
              type="button"
              variant="danger"
              size="compact"
              disabled={enCours || apercu === null}
              aria-busy={enCours}
              onClick={supprimer}
            >
              Supprimer définitivement
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
